import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import type { AiNodeActionType, AiConversationNodeData } from "@/model/interface/AiConversationNode";
import { AVAILABLE_TOOLS, CONDITION_OPERATORS } from "@/model/interface/AiConversationNode";

const nodeEditorSchema = z.object({
  message: z.string().optional(),
  questionPrompt: z.string().optional(),
  variableName: z.string().optional(),
  conditionField: z.string().optional(),
  conditionOperator: z.string().optional(),
  conditionValue: z.string().optional(),
  toolName: z.string().optional(),
  toolParameters: z.string().optional(),
  transferTarget: z.string().optional(),
  outcomeName: z.string().optional(),
});

type NodeEditorFormValues = z.infer<typeof nodeEditorSchema>;

interface AiNodeEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  actionType: AiNodeActionType;
  initialData?: Partial<AiConversationNodeData>;
  onSave: (data: Partial<AiConversationNodeData>) => void;
}

const TITLE_MAP: Record<AiNodeActionType, string> = {
  GREETING: "Greeting Message",
  SAY_MESSAGE: "Say Message",
  ASK_QUESTION: "Ask Question",
  CONDITION: "Condition Branch",
  TOOL_CALL: "Tool Call",
  TRANSFER: "Transfer Call",
  SET_OUTCOME: "Set Outcome",
  END_CONVERSATION: "End Conversation",
};

export default function AiNodeEditorDialog({
  open,
  onOpenChange,
  actionType,
  initialData,
  onSave,
}: AiNodeEditorDialogProps) {
  const form = useForm<NodeEditorFormValues>({
    resolver: zodResolver(nodeEditorSchema),
    defaultValues: {
      message: "",
      questionPrompt: "",
      variableName: "",
      conditionField: "",
      conditionOperator: "equals",
      conditionValue: "",
      toolName: "",
      toolParameters: "",
      transferTarget: "",
      outcomeName: "",
    },
  });

  useEffect(() => {
    if (initialData) {
      form.reset({
        message: initialData.message || "",
        questionPrompt: initialData.questionPrompt || "",
        variableName: initialData.variableName || "",
        conditionField: initialData.conditionField || "",
        conditionOperator: initialData.conditionOperator || "equals",
        conditionValue: initialData.conditionValue || "",
        toolName: initialData.toolName || "",
        toolParameters: initialData.toolParameters || "",
        transferTarget: initialData.transferTarget || "",
        outcomeName: initialData.outcomeName || "",
      });
    }
  }, [initialData]);

  const handleSubmit = (values: NodeEditorFormValues) => {
    onSave(values);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-125">
        <DialogHeader>
          <DialogTitle>{TITLE_MAP[actionType]}</DialogTitle>
          <DialogDescription>Configure this conversation step</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 py-2">
            {actionType === "GREETING" && (
              <FormField control={form.control} name="message" render={({ field }) => (
                <FormItem>
                  <FormLabel>Greeting Message *</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Hello! Thank you for calling. How can I help you today?"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>The first thing the agent says when the call connects</FormDescription>
                  <FormMessage />
                </FormItem>
              )} />
            )}

            {actionType === "SAY_MESSAGE" && (
              <FormField control={form.control} name="message" render={({ field }) => (
                <FormItem>
                  <FormLabel>Message *</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter what the agent should say..."
                      rows={4}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    You can use variables like {"{{caller_name}}"} or {"{{question_answer}}"}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )} />
            )}

            {actionType === "ASK_QUESTION" && (
              <>
                <FormField control={form.control} name="questionPrompt" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Question Prompt *</FormLabel>
                    <FormControl>
                      <Textarea placeholder="What is your name?" rows={3} {...field} />
                    </FormControl>
                    <FormDescription>The question the agent will ask the caller</FormDescription>
                    <FormMessage />
                  </FormItem>
                )} />

                <FormField control={form.control} name="variableName" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Store Response As</FormLabel>
                    <FormControl>
                      <Input placeholder="caller_name" {...field} />
                    </FormControl>
                    <FormDescription>Variable name to store the caller's answer for use in later steps</FormDescription>
                  </FormItem>
                )} />
              </>
            )}

            {actionType === "CONDITION" && (
              <>
                <FormField control={form.control} name="conditionField" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Variable / Field *</FormLabel>
                    <FormControl>
                      <Input placeholder="caller_intent" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />

                <FormField control={form.control} name="conditionOperator" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Operator</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        {CONDITION_OPERATORS.map((operator) => (
                          <SelectItem key={operator.value} value={operator.value}>{operator.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )} />

                <FormField control={form.control} name="conditionValue" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Value</FormLabel>
                    <FormControl>
                      <Input placeholder="schedule_appointment" {...field} />
                    </FormControl>
                    <FormDescription>The "true" output connects to one branch, "false" to another</FormDescription>
                  </FormItem>
                )} />
              </>
            )}

            {actionType === "TOOL_CALL" && (
              <>
                <FormField control={form.control} name="toolName" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tool *</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger><SelectValue placeholder="Select a tool" /></SelectTrigger></FormControl>
                      <SelectContent>
                        {AVAILABLE_TOOLS.map((tool) => (
                          <SelectItem key={tool.value} value={tool.value}>{tool.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />

                <FormField control={form.control} name="toolParameters" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Parameters (JSON)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={'{"date": "{{preferred_date}}", "time": "{{preferred_time}}"}'}
                        rows={3}
                        className="font-mono text-sm"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>JSON parameters to pass to the tool, supports variables</FormDescription>
                  </FormItem>
                )} />
              </>
            )}

            {actionType === "TRANSFER" && (
              <FormField control={form.control} name="transferTarget" render={({ field }) => (
                <FormItem>
                  <FormLabel>Transfer Target *</FormLabel>
                  <FormControl>
                    <Input placeholder="sales_team or +1234567890" {...field} />
                  </FormControl>
                  <FormDescription>A team name, user, or phone number to transfer the call to</FormDescription>
                  <FormMessage />
                </FormItem>
              )} />
            )}

            {actionType === "SET_OUTCOME" && (
              <FormField control={form.control} name="outcomeName" render={({ field }) => (
                <FormItem>
                  <FormLabel>Outcome Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="appointment_booked" {...field} />
                  </FormControl>
                  <FormDescription>Sets the call outcome/disposition for reporting</FormDescription>
                  <FormMessage />
                </FormItem>
              )} />
            )}

            {actionType === "END_CONVERSATION" && (
              <FormField control={form.control} name="message" render={({ field }) => (
                <FormItem>
                  <FormLabel>Farewell Message</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Thank you for calling. Have a great day!"
                      rows={2}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>Optional message before ending the call</FormDescription>
                </FormItem>
              )} />
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit">Save</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
